import { useState, useEffect, RefObject, useCallback } from 'react'
import { useEvent } from './useEvent'
import anylogger from '@app/anylogger'

const log = anylogger('useCaretPosition')

export interface CaretPos {
	start: number
	end: number
	isCollapsed: boolean
	rect?: DOMRect | null
}

/**
 * React hook that tracks the caret position in an editable element
 * @param ref - React ref to the editable DOM element (input, textarea, or contentEditable)
 * @returns The current caret position information
 */
export const useCaretPosition = <T extends HTMLElement>(element: HTMLElement | null): CaretPos => {
	const [caretPosition, setCaretPosition] = useState<CaretPos>({
		start: 0,
		end: 0,
		isCollapsed: true,
		rect: null
	})

	const getCaretPosition = useCallback(() => {
		let start = 0
		let end = 0
		let rect: DOMRect | null = null
		if (!element) return { start, end, rect }

		// Handle input and textarea elements
		if ('selectionStart' in element && 'selectionEnd' in element) {
			const inputElement = element as unknown as HTMLInputElement | HTMLTextAreaElement
			start = inputElement.selectionStart || 0
			end = inputElement.selectionEnd || 0

			// Try to get caret rect for input/textarea
			const selection = window.getSelection()
			if (selection && selection.rangeCount > 0) {
				const range = selection.getRangeAt(0)
				rect = range.getBoundingClientRect()
			}
			// log('start,end', start, end)
		}
		// Handle contentEditable elements
		else if (window.getSelection && 'contains' in element) {
			const selection = window.getSelection()
			if (selection && selection.rangeCount > 0) {
				const range = selection.getRangeAt(0)
				log('element', element)

				// Check if the selection is within our element
				if (element.contains(range.commonAncestorContainer)) {
					const preCaretRange = range.cloneRange()
					preCaretRange.selectNodeContents(element)
					preCaretRange.setEnd(range.startContainer, range.startOffset)
					start = preCaretRange.toString().length

					preCaretRange.setEnd(range.endContainer, range.endOffset)
					end = preCaretRange.toString().length

					rect = range.getBoundingClientRect()
				}
			}
		}
		return { start, end, rect }
	}, [element])

	const updateCaretPosition = useCallback(() => {
		if (document.activeElement !== element) return
		const cp = getCaretPosition()
		setCaretPosition((p) => {
			if (p.start == cp.start && p.end == cp.end) return p
			return {
				start: cp.start,
				end: cp.end,
				isCollapsed: cp.start == cp.end,
				rect: cp.rect
			}
		})
	}, [element, getCaretPosition])

	useEvent(element, 'click', updateCaretPosition)
	useEvent(element, 'keyup', updateCaretPosition)
	useEvent(element, 'mouseup', updateCaretPosition)
	useEvent(element, 'select', updateCaretPosition)
	// useEvent(element, 'input', updateCaretPosition)
	// useEvent(element, 'focus', updateCaretPosition)

	return caretPosition
}
